import { Link } from 'react-router-dom';
import SocialIcon from '@/components/SocialIcon';
import { PRODUCT_KEYWORDS, KEYWORD_ICONS } from '@/components/CategoryMegaMenu';
import { useLanguage } from '@/hooks/useLanguage';
import type { Tables } from '@/integrations/supabase/types';
import type { DBProduct } from '@/hooks/useProducts';

type Category = Tables<'categories'>;

interface MoreServicesMegaMenuProps {
  categories: Category[];
  /** Raw (German) products for grouping logic */
  products: DBProduct[];
  /** productId → translated title */
  titleOverrides?: Record<string, string>;
  /** productId → translated slug */
  slugOverrides?: Record<string, string>;
  onClose: () => void;
}

function getKeywordForProduct(title: string): string {
  for (const keyword of PRODUCT_KEYWORDS) {
    if (title.toLowerCase().includes(keyword.toLowerCase())) return keyword;
  }
  return '';
}

export default function MoreServicesMegaMenu({ categories, products, titleOverrides, slugOverrides, onClose }: MoreServicesMegaMenuProps) {
  const { localePath } = useLanguage();

  const categoryGroups = categories
    .map(cat => ({
      category: cat,
      products: products.filter(p => p.category_id === cat.id),
    }))
    .filter(g => g.products.length > 0);

  if (categoryGroups.length === 0) return null;

  return (
    <div className="absolute right-0 top-full z-[999] pt-1">
      <div
        className="overflow-hidden rounded-xl border bg-card shadow-xl"
        style={{ maxWidth: 'calc(100vw - 2rem)' }}
      >
        <div className="grid gap-0" style={{ gridTemplateColumns: `repeat(${Math.min(categoryGroups.length, 4)}, minmax(200px, 1fr))` }}>
          {categoryGroups.map((group, i) => (
            <div key={group.category.id} className={`p-4 ${i > 0 ? 'border-l' : ''}`}>
              <Link
                to={localePath(`/${group.category.slug}`)}
                onClick={onClose}
                className="mb-2 flex items-center gap-2 rounded-lg bg-muted/70 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-primary transition-colors hover:bg-muted"
              >
                <SocialIcon slug={group.category.slug} className="h-4 w-4" />
                {group.category.name}
              </Link>

              <div className="space-y-0.5">
                {group.products.map(product => {
                  const keyword = getKeywordForProduct(product.title);
                  return (
                    <Link
                      key={product.id}
                      to={localePath(`/produkt/${slugOverrides?.[product.id] || product.slug}`)}
                      onClick={onClose}
                      className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-accent"
                    >
                      <span className="text-muted-foreground shrink-0">
                        {KEYWORD_ICONS[keyword] || null}
                      </span>
                      <span className="text-foreground font-medium">{titleOverrides?.[product.id] || product.title}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
